import React, { ComponentPropsWithoutRef, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box, BoxProps } from '@givebutter/primitives/box';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import {
    faGift,
    faGripLines,
    faGripLinesVertical,
    faHandsHoldingHeart,
    faHeart,
    faSquare,
    faTicket,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import { useGivebutterFormContext } from 'components/form/useGivebutterFormContext';
import { FileRefMeta, UploadFile, UploadRef, UseFormUpload } from 'components/upload';
import { WidgetLivePreview } from 'components/widgets/WidgetLivePreview';
import { WIDGETS_DOCS_LINK } from 'constants/links';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twMerge } from 'tailwind-merge';
import {
    Widget,
    WidgetButtonType,
    WidgetGoalBarType,
    WidgetIcon,
    WidgetIconPosition,
    WidgetImageSizing,
    WidgetPosition,
    WidgetSignupFormChannel,
    WidgetSignupFormLayout,
    WidgetSignupFormType,
    WidgetType,
} from 'types/domain';

interface WidgetFormValues {
    campaignId: string;
    widgetName: string;
    textColor: string;
    size: string;
    type: WidgetGoalBarType | WidgetSignupFormType | WidgetButtonType | null;
    channel: WidgetSignupFormChannel | null;
    layout: WidgetSignupFormLayout | null;
    progressBarColor: string;
    backgroundColor: string;
    label: string;
    labelColor: string;
    borderColor: string;
    borderRadius: number[];
    borderWidth: number[];
    status: Widget['status'];
    position: WidgetPosition | null;
    widgetType?: WidgetType;
    icon: WidgetIcon;
    iconPosition?: WidgetIconPosition;
    dropShadow: boolean;
    showGoalAmount: boolean;
    showRaisedAmount: boolean;
    showRaisedPercentage: boolean;
    showGoalBar: boolean;
    title: string;
    description: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    buttonText: string;
    openDelay: number[];
    imageSizing?: WidgetImageSizing;
    image?: UploadFile[] | FileRefMeta[];
    isFirstAndLastNameRequired: boolean;
}

interface WidgetFormContainerProps extends BoxProps {}
const WidgetFormContainer = (props: WidgetFormContainerProps) => {
    const { className = '', ...rest } = props;

    return <Box className={twMerge(`relative flex flex-col gap-6 p-6`, className)} {...rest} />;
};

interface WidgetFormHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const WidgetFormHeader = (props: WidgetFormHeaderProps) => {
    const { className = '', ...rest } = props;

    return <div className={`flex flex-col gap-2 ${className}`} {...rest} />;
};

interface WidgetFormTitleProps extends ComponentPropsWithoutRef<'p'> {}
const WidgetFormTitle = (props: WidgetFormTitleProps) => {
    const { className = '', ...rest } = props;

    return (
        <p className={`text-2xl font-medium ${className}`} {...rest}>
            Configuration
        </p>
    );
};

interface WidgetFormDescriptionProps extends ComponentPropsWithoutRef<'p'> {}
const WidgetFormDescription = (props: WidgetFormDescriptionProps) => {
    const { className = '', ...rest } = props;
    const { getValues } = useGivebutterFormContext<WidgetFormValues>();
    const widgetType = getValues('widgetType');

    if (!widgetType) {
        return null;
    }

    return (
        <p className={`text-base text-gray-700 ${className}`} {...rest}>
            Any customizations made here will display on your page upon a refresh. Be sure to review
            your changes before clicking Publish. For advanced customizations, reference the{' '}
            <a href={WIDGETS_DOCS_LINK} target={'_blank'} rel="noreferrer">
                docs
            </a>
            .
        </p>
    );
};

const WidgetFormNameInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('widgetName')}>
                Widget name
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.TextInputRoot
                control={control}
                name={'widgetName'}
                rules={{
                    required: 'Please enter a widget name',
                }}
            >
                <Form.TextInputForm placeholder={'Element widget name'} />
            </Form.TextInputRoot>
            <Form.Error name={path('widgetName')} />
        </Form.Container>
    );
};

const WidgetFormLabelInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('label')}>Button text</Form.Label>
            <Form.TextInputRoot control={control} name={'label'}>
                <Form.TextInputForm placeholder={'Button text'} />
            </Form.TextInputRoot>
        </Form.Container>
    );
};

const WidgetSignupFormTitleInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('title')}>
                Title
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.TextInputRoot
                control={control}
                name={'title'}
                rules={{
                    required: 'Please enter a widget title',
                }}
            >
                <Form.TextInputForm />
            </Form.TextInputRoot>
            <Form.Error name={path('title')} />
        </Form.Container>
    );
};

const WidgetFormSignupFormDescriptionInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('description')}>Description</Form.Label>
            <Form.TextInputRoot control={control} name={'description'}>
                <Form.TextInputForm />
            </Form.TextInputRoot>
        </Form.Container>
    );
};

const WidgetFormSignupFormButtonTextInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('buttonText')}>
                Button text
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.TextInputRoot
                control={control}
                name={'buttonText'}
                rules={{
                    required: 'Please enter a button label',
                }}
            >
                <Form.TextInputForm />
            </Form.TextInputRoot>
            <Form.Error name={path('buttonText')} />
        </Form.Container>
    );
};

const WidgetFormBackgroundColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'backgroundColor'}>Background color</Form.Label>
            <Form.UseFormColorPicker
                name={'backgroundColor'}
                control={control}
                allowNoColor={true}
            />
        </Form.Container>
    );
};

const WidgetFormProgressBarColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'progressBarColor'}>Progress bar color</Form.Label>
            <Form.UseFormColorPicker name={'progressBarColor'} control={control} />
        </Form.Container>
    );
};

const WidgetFormTextColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'textColor'}>Text color</Form.Label>
            <Form.UseFormColorPicker name="textColor" control={control} />
        </Form.Container>
    );
};

const WidgetFormButtonBackgroundColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name="buttonBackgroundColor">Button background color</Form.Label>
            <Form.UseFormColorPicker name="buttonBackgroundColor" control={control} />
        </Form.Container>
    );
};

const WidgetFormButtonTextColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name="buttonTextColor">Button text color</Form.Label>
            <Form.UseFormColorPicker name="buttonTextColor" control={control} />
        </Form.Container>
    );
};

const WidgetFormBorderRadiusInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'borderRadius'}>Corner radius</Form.Label>
            <Form.UseFormSliderRoot control={control} name={'borderRadius'}>
                <Form.UseFormSliderInput />
                <Form.UseFormSlider max={30} step={2}>
                    <Form.UseFormSliderThumb />
                </Form.UseFormSlider>
            </Form.UseFormSliderRoot>
        </Form.Container>
    );
};

const WidgetFormSignupFormOpenDelayInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'openDelay'}>Form open delay</Form.Label>
            <Form.UseFormSliderRoot control={control} name={'openDelay'}>
                <Form.UseFormSliderInput />
                <Form.UseFormSlider max={30} step={2}>
                    <Form.UseFormSliderThumb />
                </Form.UseFormSlider>
            </Form.UseFormSliderRoot>
        </Form.Container>
    );
};

const WidgetFormSignupFormTypeInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('type')}>
                Form type <Form.LabelAsterisk />
            </Form.Label>

            <Form.UseFormRadioGroup
                name={'type'}
                control={control}
                defaultValue="static"
                variant={'card'}
            >
                <RadioGroup.Item value={'popup'}>
                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                        />
                    </RadioGroup.Indicator>
                    <Form.Label
                        className="group-radix-state-checked:text-blue-500"
                        name={path('type')}
                        htmlFor="popup"
                    >
                        Popup
                    </Form.Label>
                    <p className={'text-sm text-gray-500'}>
                        Appears as a pop up modal when a viewer lands on the page.
                    </p>
                </RadioGroup.Item>
                <RadioGroup.Item value={'static'}>
                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                        />
                    </RadioGroup.Indicator>
                    <Form.Label
                        className="group-radix-state-checked:text-blue-500"
                        name={path('type')}
                        htmlFor="static"
                    >
                        Static
                    </Form.Label>
                    <p className={'text-sm text-gray-500'}>
                        Appears as an embedded form on a static page.
                    </p>
                </RadioGroup.Item>
            </Form.UseFormRadioGroup>
        </Form.Container>
    );
};

const WidgetFormSignupFormBorderRadiusInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'borderRadius'}>Form corner radius</Form.Label>
            <Form.UseFormSliderRoot control={control} name={'borderRadius'}>
                <Form.UseFormSliderInput />
                <Form.UseFormSlider max={30} step={2}>
                    <Form.UseFormSliderThumb />
                </Form.UseFormSlider>
            </Form.UseFormSliderRoot>
        </Form.Container>
    );
};

const WidgetFormBorderWidthInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'borderWidth'}>Outline</Form.Label>

            <Form.UseFormSliderRoot control={control} name={'borderWidth'}>
                <Form.UseFormSliderInput />
                <Form.UseFormSlider max={4} step={1}>
                    <Form.UseFormSliderThumb />
                </Form.UseFormSlider>
            </Form.UseFormSliderRoot>
        </Form.Container>
    );
};

const WidgetFormBorderColorInput = () => {
    const { control, watch, setValue } = useGivebutterFormContext<WidgetFormValues>();
    const watchBorderWidth = watch('borderWidth');

    return (
        <Form.Container>
            <Form.Label name={'borderColor'}>Border color</Form.Label>
            <Form.UseFormColorPicker
                name={'borderColor'}
                control={control}
                allowNoColor={true}
                rules={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;

                        if (value !== 'transparent' && watchBorderWidth[0] === 0) {
                            setValue('borderWidth', [2]);
                        }

                        if (value === 'transparent' && watchBorderWidth[0] !== 0) {
                            setValue('borderWidth', [0]);
                        }
                    },
                }}
            />
        </Form.Container>
    );
};

const WidgetFormSelectCampaignInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();
    const { data: campaignData, ref } = useInfiniteCampaigns({ excl_type: 'external' });
    const campaigns = campaignData?.pages ?? [];

    return (
        <Form.Container>
            <Form.Label name={path('campaignId')}>
                Campaign <Form.LabelAsterisk />
            </Form.Label>
            <Form.SingleSelect
                control={control}
                name="campaignId"
                rules={{
                    required: 'Campaign is required',
                }}
            >
                <SingleSelect.Trigger aria-label="Campaign" />
                <SingleSelect.Content>
                    {campaigns.map(campaign => (
                        <SingleSelect.Item value={campaign.id.toString()} key={campaign.id}>
                            <SingleSelect.ItemText>{campaign.title}</SingleSelect.ItemText>
                        </SingleSelect.Item>
                    ))}
                    <div ref={ref} className="h-4" />
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormLivePreview = () => {
    const { watch } = useGivebutterFormContext<WidgetFormValues>();
    const formValues = watch();

    const match = useRouteMatch<{ campaign_id?: string }>();
    const campaignIdURL = match.params.campaign_id;
    const campaignId = Number(campaignIdURL ?? formValues.campaignId);
    const { data: campaign } = useSuspenseQuery({
        queryKey: campaignQueryKeys.campaign(campaignId),
        queryFn: () => conditionallyFetchOrNull(() => fetchCampaign({ campaignId }), !!campaignId),
    });

    const uploadedFileBlob = (formValues.image?.[0] as UploadFile)?.file;
    const uploadedFile =
        uploadedFileBlob instanceof Blob
            ? URL.createObjectURL(uploadedFileBlob)
            : formValues.image?.[0]?.url;

    return (
        <div className={'flex flex-col gap-4'}>
            <p className={'text-xl font-medium'}>Preview</p>
            <div className={'flex h-full items-center justify-center bg-gray-50 p-5'}>
                <WidgetLivePreview
                    type={formValues.widgetType}
                    campaign={campaign}
                    label={formValues.label}
                    backgroundColor={formValues.backgroundColor}
                    borderWidth={formValues.borderWidth.toString()}
                    borderColor={formValues.borderColor}
                    labelColor={formValues.labelColor}
                    borderRadius={formValues.borderRadius.toString()}
                    icon={formValues.icon}
                    iconPosition={formValues.iconPosition}
                    dropShadow={formValues.dropShadow}
                    textColor={formValues.textColor}
                    progressBarColor={formValues.progressBarColor}
                    size={formValues.size}
                    goalBarType={formValues.type ?? undefined}
                    showGoalAmount={formValues.showGoalAmount}
                    showRaisedAmount={formValues.showRaisedAmount}
                    showRaisedPercentage={formValues.showRaisedPercentage}
                    showGoalBar={formValues.showGoalBar}
                    title={formValues.title}
                    description={formValues.description}
                    layout={formValues.layout ?? undefined}
                    buttonText={formValues.buttonText}
                    buttonTextColor={formValues.buttonTextColor}
                    buttonBackgroundColor={formValues.buttonBackgroundColor}
                    channel={formValues.channel ?? undefined}
                    image={uploadedFile}
                    imageSizing={formValues.imageSizing}
                />
            </div>
        </div>
    );
};

const WidgetFormLabelColorInput = () => {
    const { control } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={'labelColor'}>Label color</Form.Label>
            <Form.UseFormColorPicker name={'labelColor'} control={control} />
        </Form.Container>
    );
};

const WidgetFormButtonTypeInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('type')}>Button type</Form.Label>
            <Form.SingleSelect control={control} name="type">
                <SingleSelect.Trigger aria-label="Button type" />
                <SingleSelect.Content>
                    <SingleSelect.Item
                        value="static"
                        className="max-w-[--radix-select-trigger-width]"
                    >
                        <div className="flex flex-col gap-2">
                            <p className="font-medium text-gray-900">
                                <SingleSelect.ItemText>Static</SingleSelect.ItemText>
                            </p>
                            <p className="font-normal text-gray-700">
                                Static buttons only appear where they are placed on your website.
                            </p>
                        </div>
                    </SingleSelect.Item>
                    <SingleSelect.Item
                        value="floating"
                        className="max-w-[--radix-select-trigger-width]"
                    >
                        <div className="flex flex-col gap-2">
                            <span className="font-medium text-gray-900">
                                <SingleSelect.ItemText>Floating</SingleSelect.ItemText>
                            </span>
                            <span className="font-normal text-gray-700">
                                A floating button is viewable anywhere a viewer scrolls on the page.
                            </span>
                        </div>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormPositionInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('position')}>Position</Form.Label>
            <Form.SingleSelect control={control} name="position">
                <SingleSelect.Trigger aria-label="Position" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="top-left">
                        <SingleSelect.ItemText>Top left</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="middle-left">
                        <SingleSelect.ItemText>Middle left</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="bottom-left">
                        <SingleSelect.ItemText>Bottom left</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="top-right">
                        <SingleSelect.ItemText>Top right</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="middle-right">
                        <SingleSelect.ItemText>Middle right</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="bottom-right">
                        <SingleSelect.ItemText>Bottom right</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormGoalBarTypeInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('type')}>Goal bar type</Form.Label>
            <Form.SingleSelect control={control} name="type">
                <SingleSelect.Trigger aria-label="Type" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="progress-bar">
                        <SingleSelect.ItemText>Progress bar</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="thermometer">
                        <SingleSelect.ItemText>Thermometer</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormSizeInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('size')}>Size</Form.Label>
            <Form.SingleSelect control={control} name="size">
                <SingleSelect.Trigger aria-label="Size" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="sm">
                        <SingleSelect.ItemText>Small</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="md">
                        <SingleSelect.ItemText>Medium</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="all">
                        <SingleSelect.ItemText>Large</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormIconInput = () => {
    const { control, path, watch } = useGivebutterFormContext<WidgetFormValues>();
    const icon = watch('icon');

    return (
        <div className="flex flex-col gap-4">
            <Form.Container>
                <Form.Label name={path('icon')}>Icon</Form.Label>
                <Form.SingleSelect control={control} name="icon">
                    <SingleSelect.Trigger aria-label="Icon" />
                    <SingleSelect.Content>
                        <SingleSelect.Item value="heart">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faHeart}
                                        className="size-4 text-gray-500"
                                    />
                                    Heart
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="gift">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faGift}
                                        className="size-4 text-gray-500"
                                    />
                                    Gift
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="giving_hands">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faHandsHoldingHeart}
                                        className="size-4 text-gray-500"
                                    />
                                    Giving hands
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="ticket">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faTicket}
                                        className="size-4 text-gray-500"
                                    />
                                    Ticket
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="none">
                            <SingleSelect.ItemText>No icon</SingleSelect.ItemText>
                        </SingleSelect.Item>
                    </SingleSelect.Content>
                </Form.SingleSelect>
                <Form.Error name={path('icon')} />
            </Form.Container>

            {icon !== 'none' && (
                <Form.Container className={'flex flex-col gap-2'}>
                    <Form.Label name={path('iconPosition')}>Icon alignment</Form.Label>
                    <Form.UseFormRadioGroup
                        name={'iconPosition'}
                        control={control}
                        rules={{ required: 'The icon alignment is required.' }}
                        className={'h-5 justify-start gap-x-6'}
                    >
                        <RadioGroup.ItemContainer>
                            <RadioGroup.Item value={'left'}>
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <Form.Label
                                name={path('iconPosition')}
                                htmlFor={'left'}
                                className={'leading-none'}
                            >
                                Left
                            </Form.Label>
                        </RadioGroup.ItemContainer>
                        <RadioGroup.ItemContainer>
                            <RadioGroup.Item value={'right'}>
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <Form.Label
                                name={path('iconPosition')}
                                htmlFor={'right'}
                                className={'leading-none'}
                            >
                                Right
                            </Form.Label>
                        </RadioGroup.ItemContainer>
                    </Form.UseFormRadioGroup>
                    <Form.Error name={path('iconPosition')} />
                </Form.Container>
            )}
        </div>
    );
};

const WidgetFormLayoutInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <div className="flex flex-col gap-4">
            <Form.Container>
                <Form.Label name={path('layout')}>Layout</Form.Label>
                <Form.SingleSelect control={control} name="layout">
                    <SingleSelect.Trigger aria-label="Layout" />
                    <SingleSelect.Content>
                        <SingleSelect.Item value="stacked">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faGripLines}
                                        className="size-4 text-gray-500"
                                    />
                                    Stacked
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="two-column">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faGripLinesVertical}
                                        className="size-4 text-gray-500"
                                    />
                                    Two-column
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="simple">
                            <SingleSelect.ItemText>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faSquare}
                                        className="size-4 text-gray-500"
                                    />
                                    Simple
                                </span>
                            </SingleSelect.ItemText>
                        </SingleSelect.Item>
                    </SingleSelect.Content>
                </Form.SingleSelect>
                <Form.Error name={path('layout')} />
            </Form.Container>
        </div>
    );
};

const WidgetFormShadowInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container className={'flex flex-col gap-y-1'}>
            <div className={'flex justify-between'}>
                <div className={'flex flex-col gap-y-1'}>
                    <Form.Label name="dropShadow">Shadow</Form.Label>
                    <p className={'text-sm text-gray-600'}>
                        Enable to give your donation button a background shadow.
                    </p>
                </div>
                <Form.Switch name={'dropShadow'} control={control} />
            </div>
            <Form.Error name={path('dropShadow')} />
        </Form.Container>
    );
};

const WidgetFormDisplayGoalBarInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container className={'flex flex-col gap-y-1'}>
            <div className={'flex justify-between'}>
                <div className={'flex flex-col gap-y-1'}>
                    <Form.Label name="showGoalBar">Goal bar</Form.Label>
                    <p className={'text-sm text-gray-600'}>
                        Display a progress bar to show how close you are to your goal.
                    </p>
                </div>
                <Form.Switch name={'showGoalBar'} control={control} />
            </div>
            <Form.Error name={path('showGoalBar')} />
        </Form.Container>
    );
};

const WidgetFormSignupRequiredFields = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container className={'flex flex-col gap-y-1'}>
            <div className={'flex justify-between'}>
                <div className={'flex flex-col gap-y-1'}>
                    <Form.Label name="isFirstAndLastNameRequired">First and last name</Form.Label>
                    <p className={'text-sm text-gray-600'}>Make first and last name required.</p>
                </div>
                <Form.Switch name={'isFirstAndLastNameRequired'} control={control} />
            </div>
            <Form.Error name={path('isFirstAndLastNameRequired')} />
        </Form.Container>
    );
};

const WidgetFormCommunicationChannelInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('channel')}>Communication channel</Form.Label>
            <Form.SingleSelect control={control} name="channel">
                <SingleSelect.Trigger aria-label="Channel" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="email">
                        <SingleSelect.ItemText>Email</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="phone">
                        <SingleSelect.ItemText>Phone number</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="all">
                        <SingleSelect.ItemText>Email and phone number</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormSignupFormImageSizingInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('imageSizing')}>Image sizing</Form.Label>
            <Form.SingleSelect control={control} name="imageSizing">
                <SingleSelect.Trigger aria-label="Image sizing" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="fill">
                        <SingleSelect.ItemText>Fill</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="fit">
                        <SingleSelect.ItemText>Fit</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="stretch">
                        <SingleSelect.ItemText>Stretch</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const WidgetFormImageInput = () => {
    const { control, path } = useGivebutterFormContext<WidgetFormValues>();
    const uploadRef = useRef<UploadRef>(null);

    return (
        <Form.Container>
            <Form.Label name={path('image')}>Upload an image</Form.Label>
            <UseFormUpload
                variant={'single'}
                inputRef={uploadRef}
                name={'image'}
                control={control}
                acceptedFileTypes={['image/*']}
                strategy={'remote'}
                allowReorder={false}
                allowMultiple={false}
                allowReplace={true}
                labelIdle={'Click or drag file'}
            />
        </Form.Container>
    );
};

const transformWidgetTypeToReadable = (type: WidgetType) => {
    switch (type) {
        case 'giving-form':
            return 'Form';
        case 'signup-form':
            return 'Signup form';
        case 'goal-bar':
            return 'Goal bar';
        case 'button':
        default:
            return 'Button';
    }
};

const Container = WidgetFormContainer;
const Header = WidgetFormHeader;
const Title = WidgetFormTitle;
const Description = WidgetFormDescription;
const NameInput = WidgetFormNameInput;
const LabelInput = WidgetFormLabelInput;
const BackgroundColorInput = WidgetFormBackgroundColorInput;
const ProgressBarColor = WidgetFormProgressBarColorInput;
const TextColorInput = WidgetFormTextColorInput;
const ButtonBackgroundColorInput = WidgetFormButtonBackgroundColorInput;
const ButtonTextColorInput = WidgetFormButtonTextColorInput;
const CampaignInput = WidgetFormSelectCampaignInput;
const ButtonTypeInput = WidgetFormButtonTypeInput;
const PositionInput = WidgetFormPositionInput;
const GoalBarTypeInput = WidgetFormGoalBarTypeInput;
const SizeInput = WidgetFormSizeInput;
const BorderRadiusInput = WidgetFormBorderRadiusInput;
const BorderWidthInput = WidgetFormBorderWidthInput;
const BorderColorInput = WidgetFormBorderColorInput;
const LabelColorInput = WidgetFormLabelColorInput;
const IconInput = WidgetFormIconInput;
const ShadowInput = WidgetFormShadowInput;
const Preview = WidgetFormLivePreview;
const DisplayGoalBarInput = WidgetFormDisplayGoalBarInput;
const SignupRequiredFields = WidgetFormSignupRequiredFields;
const CommunicationChannelInput = WidgetFormCommunicationChannelInput;
const SignupFormTitleInput = WidgetSignupFormTitleInput;
const SignupFormDescriptionInput = WidgetFormSignupFormDescriptionInput;
const SignupFormTypeInput = WidgetFormSignupFormTypeInput;
const SignupFormOpenDelayInput = WidgetFormSignupFormOpenDelayInput;
const SignupFormImageSizingInput = WidgetFormSignupFormImageSizingInput;
const SignupFormButtonTextInput = WidgetFormSignupFormButtonTextInput;
const SignupFormBorderRadiusInput = WidgetFormSignupFormBorderRadiusInput;
const LayoutInput = WidgetFormLayoutInput;
const ImageInput = WidgetFormImageInput;

export {
    Container,
    Header,
    Title,
    Description,
    NameInput,
    LabelInput,
    BackgroundColorInput,
    ProgressBarColor,
    TextColorInput,
    ButtonBackgroundColorInput,
    ButtonTextColorInput,
    CampaignInput,
    ButtonTypeInput,
    PositionInput,
    GoalBarTypeInput,
    SizeInput,
    BorderRadiusInput,
    BorderWidthInput,
    BorderColorInput,
    LabelColorInput,
    IconInput,
    ShadowInput,
    Preview,
    DisplayGoalBarInput,
    SignupRequiredFields,
    CommunicationChannelInput,
    SignupFormTitleInput,
    SignupFormDescriptionInput,
    SignupFormTypeInput,
    SignupFormOpenDelayInput,
    SignupFormImageSizingInput,
    SignupFormButtonTextInput,
    SignupFormBorderRadiusInput,
    LayoutInput,
    ImageInput,
    //
    WidgetFormContainer,
    WidgetFormHeader,
    WidgetFormNameInput,
    WidgetFormLabelInput,
    WidgetFormBackgroundColorInput,
    WidgetFormProgressBarColorInput,
    WidgetFormTextColorInput,
    WidgetFormButtonBackgroundColorInput,
    WidgetFormButtonTextColorInput,
    WidgetFormTitle,
    WidgetFormDescription,
    WidgetFormSelectCampaignInput,
    WidgetFormButtonTypeInput,
    WidgetFormPositionInput,
    WidgetFormGoalBarTypeInput,
    WidgetFormSizeInput,
    WidgetFormBorderRadiusInput,
    WidgetFormBorderWidthInput,
    WidgetFormBorderColorInput,
    WidgetFormLabelColorInput,
    WidgetFormIconInput,
    WidgetFormShadowInput,
    WidgetFormLivePreview,
    WidgetFormDisplayGoalBarInput,
    WidgetFormSignupRequiredFields,
    WidgetSignupFormTitleInput,
    WidgetFormSignupFormDescriptionInput,
    WidgetFormCommunicationChannelInput,
    WidgetFormSignupFormTypeInput,
    WidgetFormSignupFormOpenDelayInput,
    WidgetFormSignupFormImageSizingInput,
    WidgetFormSignupFormButtonTextInput,
    WidgetFormSignupFormBorderRadiusInput,
    WidgetFormLayoutInput,
    WidgetFormImageInput,
    //
    transformWidgetTypeToReadable,
};
export type { WidgetFormValues };
